/* ==========================================================================
   Trufairs Trends v2 — shared behaviour + analytics hooks
   Vanilla JS, no dependencies, no global namespace pollution beyond
   window.TFTrends (single readonly-ish handle used for the init guard).

   Loaded once per page. Because every Trends section renders the same
   <script src> tag, this file may be evaluated more than once — the guard
   below makes runs 2..N no-ops.

   Analytics: every interactive hook carries data-tf-event="<name>".
   On activation we
     1. dispatch a CustomEvent(<name>) on document (bubbling, detail = payload)
     2. push { event: <name>, ...payload } to window.dataLayer IF it exists
   No personal data is ever collected or transmitted. See
   documentation/ANALYTICS-EVENTS.md.

   To disable analytics entirely, set window.TF_TRENDS_DISABLE_ANALYTICS = true
   before this file executes.
   ========================================================================== */
(function () {
  'use strict';

  if (window.TFTrends && window.TFTrends.version) return;

  var VERSION = '1.0.0';
  var ROOT_SELECTOR = '.tf-trends';

  /* ---------------------------------------------------------------- utils */

  function qsa(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function analyticsEnabled() {
    return window.TF_TRENDS_DISABLE_ANALYTICS !== true;
  }

  /**
   * Emit one analytics event. `payload` must contain non-personal data only.
   */
  function emit(name, payload) {
    if (!name || !analyticsEnabled()) return;

    var detail = payload || {};

    try {
      document.dispatchEvent(
        new CustomEvent(name, { bubbles: true, cancelable: false, detail: detail })
      );
    } catch (e) {
      /* CustomEvent unsupported — analytics is strictly optional */
    }

    if (Array.isArray(window.dataLayer)) {
      var entry = { event: name };
      for (var key in detail) {
        if (Object.prototype.hasOwnProperty.call(detail, key)) entry[key] = detail[key];
      }
      window.dataLayer.push(entry);
    }
  }

  /**
   * Read data-tf-* attributes off an element into an analytics payload.
   * Only whitelisted, non-personal keys are forwarded.
   */
  var PAYLOAD_KEYS = [
    'sectionId',
    'sectionType',
    'blockId',
    'productId',
    'variantId',
    'articleId',
    'label',
    'position',
    'listName',
    'videoType'
  ];

  function payloadFrom(el, extra) {
    var out = {};
    if (el && el.dataset) {
      PAYLOAD_KEYS.forEach(function (key) {
        var value = el.dataset['tf' + key.charAt(0).toUpperCase() + key.slice(1)];
        if (value !== undefined && value !== '') out[key] = value;
      });
    }
    if (extra) {
      for (var k in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, k)) out[k] = extra[k];
      }
    }
    return out;
  }

  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* ------------------------------------------------- delegated click hooks */
  /* One document-level listener handles every section, so section reloads in
     the Theme Editor never register duplicate handlers. */

  function onDocumentClick(event) {
    var trigger = event.target.closest
      ? event.target.closest('[data-tf-event]')
      : null;
    if (!trigger) return;
    if (!trigger.closest(ROOT_SELECTOR)) return;

    /* Elements with a specialised handler emit their own, richer payload.
       Bailing out here is what keeps the event from firing twice. */
    if (trigger.hasAttribute('data-tf-video-facade')) return;

    emit(trigger.dataset.tfEvent, payloadFrom(trigger));
  }

  /* ------------------------------------------------ family style switching */

  function familyCardFrom(button) {
    return button.closest('[data-tf-family-card]');
  }

  function handleSwatch(button) {
    var card = familyCardFrom(button);
    if (!card) return;

    var image = card.querySelector('[data-tf-family-image]');
    var link = card.querySelector('[data-tf-family-link]');
    var swatches = qsa('[data-tf-family-swatch]', card);

    var nextSrc = button.dataset.tfImage;
    var nextSrcset = button.dataset.tfSrcset;
    var nextAlt = button.dataset.tfAlt;
    var nextUrl = button.dataset.tfUrl;

    if (image && nextSrc) {
      image.src = nextSrc;
      if (nextSrcset) image.srcset = nextSrcset;
      if (nextAlt) image.alt = nextAlt;
      /* The swapped-in image is already downloaded by the swatch itself. */
      image.removeAttribute('loading');
    }

    if (link && nextUrl) link.href = nextUrl;

    swatches.forEach(function (swatch) {
      swatch.setAttribute('aria-pressed', swatch === button ? 'true' : 'false');
    });

    emit(
      'tf_trends_family_style_change',
      payloadFrom(card, {
        variantId: button.dataset.tfVariantId || '',
        position: button.dataset.tfPosition || ''
      })
    );
  }

  function onSwatchClick(event) {
    var button = event.target.closest
      ? event.target.closest('[data-tf-family-swatch]')
      : null;
    if (!button) return;

    event.preventDefault();
    handleSwatch(button);
  }

  /* ------------------------------------------------- autoplay-capable video */

  function initAutoplayVideo(video) {
    if (video.dataset.tfReady === 'true') return;
    video.dataset.tfReady = 'true';

    /* Autoplay is opt-in via markup and is always muted + inline.
       Under reduced-motion we never start playback: the poster stands in. */
    if (video.dataset.tfAutoplay !== 'true' || prefersReducedMotion()) return;

    video.muted = true;
    video.setAttribute('muted', '');
    var attempt = video.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt.then(
        function () {
          /* Playing already — drop the cover treatment and the play button so
             they are not sitting on top of a running video. */
          var wrap = video.closest('[data-tf-video]');
          if (wrap) {
            wrap.classList.add('is-playing');
            var btn = wrap.querySelector('[data-tf-video-play]');
            if (btn) btn.remove();
            if (video.dataset.tfControls !== 'false') video.controls = true;
          }
        },
        function () {
          /* Browser refused autoplay — the poster and play button remain. */
        }
      );
    }
  }

  /* ------------------------------------ hosted video: click to play, unmuted */

  /* The visitor tapped a play button. That tap IS the user gesture browsers
     require, so sound is allowed here — this is the one place we unmute.
     Autoplay is the opposite case and stays muted; see initAutoplayVideo. */
  function onVideoPlayClick(event) {
    var button = event.target.closest
      ? event.target.closest('[data-tf-video-play]')
      : null;
    if (!button) return;

    event.preventDefault();

    var wrap = button.closest('[data-tf-video]');
    var video = wrap ? wrap.querySelector('video') : null;
    if (!video) return;

    /* Controls are withheld until now so the native bar does not sit under the
       cover image beside our own button. */
    if (video.dataset.tfControls !== 'false') video.controls = true;
    if (video.dataset.tfAutoplay !== 'true') video.muted = false;

    wrap.classList.add('is-playing');

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        /* Some mobile browsers still refuse unmuted playback in an iframe or
           a low-power mode. Falling back to muted beats failing silently —
           the visitor can unmute from the controls we just switched on. */
        video.muted = true;
        var retry = video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(function () {});
      });
    }

    button.remove();
  }

  /* ------------------------------------------- hosted video play analytics */

  function initHostedVideo(video) {
    if (video.dataset.tfPlayBound === 'true') return;
    video.dataset.tfPlayBound = 'true';

    video.addEventListener(
      'play',
      function () {
        emit(
          video.dataset.tfEvent || 'tf_trends_jennifer_video_play',
          payloadFrom(video, { videoType: 'shopify' })
        );
      },
      { once: true }
    );
  }

  /* ------------------------------------------------- external video facade */
  /* Third-party players load only after an explicit user action. */

  function onFacadeClick(event) {
    var button = event.target.closest
      ? event.target.closest('[data-tf-video-facade]')
      : null;
    if (!button) return;

    event.preventDefault();

    var wrap = button.closest('[data-tf-video]');
    var src = button.dataset.tfEmbedSrc;
    if (!wrap || !src) return;

    var iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.title = button.dataset.tfEmbedTitle || 'Video';
    iframe.setAttribute('frameborder', '0');
    /* Only what playback actually needs — a narrower allow-list keeps the
       console clean and grants the third-party frame no surplus capability. */
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');

    wrap.appendChild(iframe);
    /* Drops the poster scrim and the duration/captions chips, so they do not
       sit on top of the player once it is in. */
    wrap.classList.add('is-playing');
    button.remove();
    iframe.focus();

    emit(
      button.dataset.tfEvent || 'tf_trends_jennifer_video_play',
      payloadFrom(button, { videoType: button.dataset.tfVideoType || 'external' })
    );
  }

  /* ----------------------------------------------------- newsletter form */

  function initNewsletter(form) {
    if (form.dataset.tfReady === 'true') return;
    form.dataset.tfReady = 'true';

    var field = form.querySelector('[data-tf-news-email]');
    var feedback = form.querySelector('[data-tf-news-feedback]');
    var invalidMessage = form.dataset.tfInvalidMessage || 'Please enter a valid email address.';

    if (field) {
      field.addEventListener('input', function () {
        if (field.getAttribute('aria-invalid') !== 'true') return;
        if (!field.validity.valid) return;
        field.setAttribute('aria-invalid', 'false');
        if (feedback && feedback.dataset.tfState === 'client-error') {
          feedback.textContent = '';
          feedback.className = 'tf-trends-news__feedback';
          delete feedback.dataset.tfState;
        }
      });
    }

    form.addEventListener('submit', function (event) {
      if (field && !field.validity.valid) {
        event.preventDefault();
        field.setAttribute('aria-invalid', 'true');
        if (feedback) {
          feedback.textContent = invalidMessage;
          feedback.className = 'tf-trends-news__feedback tf-trends-news__feedback--error';
          feedback.dataset.tfState = 'client-error';
        }
        field.focus();
        return;
      }

      if (field) field.setAttribute('aria-invalid', 'false');
      /* Deliberately no email address in the payload. */
      emit('tf_trends_newsletter_submit', payloadFrom(form));
    });
  }

  /* ------------------------------------------------------ reveal on scroll */

  /* The CSS that hides a [data-tf-reveal] element is gated behind
     .tf-trends--reveal, and only this function ever adds that class. So if the
     script never runs, nothing is ever hidden and the page renders in full.
     Do not move the hiding rule out from behind that class. */
  var revealObserver = null;

  function revealNow(el) {
    el.classList.add('is-revealed');
  }

  function initReveal(root) {
    var targets = qsa('[data-tf-reveal]', root);
    if (!targets.length) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach(revealNow);
      return;
    }

    qsa(ROOT_SELECTOR, root).forEach(function (scope) {
      scope.classList.add('tf-trends--reveal');
    });
    if (root.matches && root.matches(ROOT_SELECTOR)) {
      root.classList.add('tf-trends--reveal');
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            revealNow(entry.target);
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
    }

    targets.forEach(function (el) {
      revealObserver.observe(el);
    });

    /* Safety net: never leave content hidden if the observer misfires
       (print, an offscreen container, an aggressive extension). */
    window.setTimeout(function () {
      targets.forEach(revealNow);
    }, 4000);
  }

  /* --------------------------------------------------------------- init */

  function initScope(scope) {
    var root = scope || document;

    qsa('[data-tf-autoplay-video]', root).forEach(initAutoplayVideo);
    qsa('[data-tf-hosted-video]', root).forEach(initHostedVideo);
    qsa('[data-tf-jennifer-video]', root).forEach(initHostedVideo);
    qsa('[data-tf-news-form]', root).forEach(initNewsletter);
    initReveal(root);
  }

  function bindGlobalListeners() {
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('click', onSwatchClick);
    document.addEventListener('click', onFacadeClick);
    document.addEventListener('click', onVideoPlayClick);

    /* Shopify Theme Editor lifecycle — re-init only the affected section. */
    document.addEventListener('shopify:section:load', function (event) {
      initScope(event.target);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      /* Stop any media the removed section left playing. */
      qsa('video', event.target).forEach(function (video) {
        try {
          video.pause();
        } catch (e) {
          /* nothing to clean up */
        }
      });
    });

    document.addEventListener('shopify:block:select', function (event) {
      var block = event.target;
      if (!block || !block.closest || !block.closest(ROOT_SELECTOR)) return;
      if (typeof block.scrollIntoView === 'function') {
        block.scrollIntoView({
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          block: 'center'
        });
      }
    });
  }

  function start() {
    bindGlobalListeners();
    initScope(document);
  }

  window.TFTrends = {
    version: VERSION,
    emit: emit,
    init: initScope
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
